import React from 'react'
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext'
import { Shell } from './components/Shell'
import { GodModeShell } from './components/GodModeShell'
import { ClientsPage } from './pages/godmode/ClientsPage'
import { DashboardPage as GodDashboardPage } from './pages/godmode/DashboardPage'
import { QueuesPage } from './pages/godmode/QueuesPage'
import { ApiPage } from './pages/godmode/ApiPage'
import { NewClientPage } from './pages/godmode/NewClientPage'
import { ClientDetailPage } from './pages/godmode/ClientDetailPage'
import { SharedTagsPage } from './pages/godmode/SharedTagsPage'
import { LegacyPage } from './pages/godmode/LegacyPage'
import { TagStockPage } from './pages/godmode/TagStockPage'
import { TagsPage } from './pages/godmode/TagsPage'
import { WorkerCardsPage } from './pages/godmode/WorkerCardsPage'
import { RefusalsPage } from './pages/godmode/RefusalsPage'
import { QcUnmatchedPage } from './pages/godmode/QcUnmatchedPage'
import { ClientSetupPage } from './pages/godmode/ClientSetupPage'
import { SignInPage } from './pages/SignInPage'
import { NoAccessPage } from './pages/NoAccessPage'
import { InviteProblemPage, InviteSignIn } from './pages/InvitePage'
import { isInvitePath } from './lib/invite'
import { DevicesPage } from './pages/DevicesPage'
import { DevicePairPage, DevicePairSignIn, isDevicePairPath } from './pages/DevicePairPage'
import { WorkspacePickerPage } from './pages/WorkspacePickerPage'
import { DashboardPage } from './pages/DashboardPage'
import { PersonnelPage } from './pages/PersonnelPage'
import { GroupsPage } from './pages/GroupsPage'
import { TeamsPage } from './pages/TeamsPage'
import { IntegrationsPage } from './pages/IntegrationsPage'
import { ApiKeysPage } from './pages/ApiKeysPage'
import { GeneralSettingsPage } from './pages/GeneralSettingsPage'
import { SitesPage } from './pages/SitesPage'
import { SitesMapPage } from './pages/SitesMapPage'
import { PickingJobsPage } from './pages/PickingJobsPage'
import { LabelPrintingPage } from './pages/LabelPrintingPage'
import { StockDispatchPage } from './pages/StockDispatchPage'
import { StockSettingsPage } from './pages/stock/StockSettingsPage'
import { OverviewPage } from './pages/stock/OverviewPage'
import { ModulePreviewPage } from './pages/stock/ModulePreviewPage'
import { TimesheetsPage } from './pages/TimesheetsPage'
import { QueriesPage } from './pages/QueriesPage'
import { ProductActivityPage } from './pages/ProductActivityPage'
import { BatchesPage } from './pages/BatchesPage'
import { WagesPage } from './pages/WagesPage'
import { ReportsPage } from './pages/ReportsPage'
import { QualityControlPage } from './pages/QualityControlPage'
import { WallboardPage } from './pages/reports/WallboardPage'
import { SharedReportPage } from './pages/SharedReportPage'
import { CrewAppPage } from './pages/CrewAppPage'
import { CrewWebPage } from './pages/CrewWebPage'
import { FirebaseConfigErrorPage } from './pages/FirebaseConfigErrorPage'
import { configError } from './lib/firebase'
import { AccessGate } from './components/AccessGate'
import { landingPath, screenOf, useAccess } from './lib/access'
import { EnvBanner } from './components/EnvBanner'

// A data router, so a navigation can be held while a panel has unsaved
// changes (h 454-456; layers.tsx NavigationGuard). Every route below it is
// still declared with <Routes>, as before. Made on first render, not on
// import, so the tests that import AppRoutes start no router of their own.
let router: ReturnType<typeof createBrowserRouter> | null = null

function App() {
  router ??= createBrowserRouter([{ path: '*', element: <Root /> }])
  return (
    <>
      <EnvBanner />
      <RouterProvider router={router} />
    </>
  )
}

function Root() {
  return (
    <Routes>
      {/* A scheduled report's link (§7l.6): no sign-in, no Firebase, no workspace. */}
      <Route path="/shared/report/:token" element={<SharedReportPage />} />
      {/* A clock-on QR scanned with a phone camera: the Crew app's store page. */}
      <Route path="/crewapp" element={<CrewAppPage />} />
      <Route path="/crew" element={<CrewWebPage />} />

      {/* Firebase config error - shown before auth */}
      {configError && <Route path="*" element={<FirebaseConfigErrorPage error={configError} />} />}

      {/* Auth and app routes */}
      {!configError && <Route path="*" element={<AppWithAuth />} />}
    </Routes>
  )
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <AppRoutes />
      </WorkspaceProvider>
    </AuthProvider>
  )
}

// Exported for the workspace-routing tests, which run it under a MemoryRouter.
export function AppRoutes() {
  const { status } = useAuth()
  const { currentWorkspace, subscriptions, isLoading: workspaceLoading, pickerOpen, staff, invite } = useWorkspace()
  const location = useLocation()
  const access = useAccess()

  // Still loading auth state, or signed in and still asking /api/me which
  // workspaces this account has (without this, NoAccessPage flashes first).
  if (status === 'loading' || (status === 'signed-in' && workspaceLoading)) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading...</p>
      </div>
    )
  }

  // Firebase not configured
  if (status === 'firebase-not-configured') {
    return <FirebaseConfigErrorPage error={new Error('Firebase not configured')} />
  }

  // Not signed in - show sign-in page
  if (status === 'signed-out') {
    return (
      <Routes>
        {/* An invitation link (Invite system user): the sign-in screen says what it is for. */}
        <Route path="/invite/:token" element={<InviteSignIn />} />
        {/* A Dataphyll Field tablet's QR: the sign-in screen says what it is for. */}
        <Route path="/devices/pair/:code" element={<DevicePairSignIn />} />
        <Route path="*" element={<SignInPage />} />
      </Routes>
    )
  }

  // Signed in at an invitation link: WorkspaceContext has taken it up already. A refusal says why; otherwise the
  // workspace it joined is chosen, and the link gives way to the app.
  if (isInvitePath(location.pathname)) {
    if (invite && !invite.ok) {
      return <InviteProblemPage message={invite.message || 'The invitation could not be taken up.'} canContinue={staff || subscriptions.length > 0} />
    }
    return <Navigate to="/" replace />
  }

  // A Dataphyll Field tablet's QR, signed in: register it in one of the caller's workspaces. Before God Mode and the
  // workspace picker, since the page lists every workspace itself.
  if (isDevicePairPath(location.pathname)) {
    return (
      <Routes>
        <Route path="/devices/pair/:code" element={<DevicePairPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // God Mode (Dataphyll staff): at its own addresses, and the only place a
  // staff member with no workspace yet can go (a new database, before its
  // first client). The API refuses it to anyone else.
  if (staff && (subscriptions.length === 0 || location.pathname.startsWith('/god-mode'))) {
    return (
      <Routes>
        <Route
          path="/god-mode/*"
          element={
            <GodModeShell>
              <Routes>
                <Route path="/" element={<GodDashboardPage />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/new" element={<NewClientPage />} />
                <Route path="/clients/:id" element={<ClientDetailPage />} />
                <Route path="/clients/:id/cards" element={<WorkerCardsPage />} />
                <Route path="/clients/:id/refusals" element={<RefusalsPage />} />
                <Route path="/clients/:id/qc" element={<QcUnmatchedPage />} />
                <Route path="/clients/:id/setup" element={<ClientSetupPage />} />
                <Route path="/tags" element={<SharedTagsPage />} />
                <Route path="/legacy" element={<LegacyPage />} />
                <Route path="/stock" element={<TagStockPage />} />
                <Route path="/all-tags" element={<TagsPage />} />
                <Route path="/queues" element={<QueuesPage />} />
                <Route path="/api" element={<ApiPage />} />
                {/* Queued email is the Emails tab of Queues now. */}
                <Route path="/emails" element={<Navigate to="/god-mode/queues?tab=emails" replace />} />
                <Route path="*" element={<Navigate to="/god-mode" replace />} />
              </Routes>
            </GodModeShell>
          }
        />
        <Route path="*" element={<Navigate to="/god-mode" replace />} />
      </Routes>
    )
  }

  // Signed in but no workspace access
  if (!currentWorkspace && subscriptions.length === 0) {
    return (
      <Routes>
        <Route path="*" element={<NoAccessPage />} />
      </Routes>
    )
  }

  // handoff §5: 2+ subscriptions and none chosen yet, or the sidebar brand
  // block reopened it. A screen at whatever URL was open, not a modal.
  if (!currentWorkspace || pickerOpen) {
    return (
      <Routes>
        <Route path="*" element={<WorkspacePickerPage />} />
      </Routes>
    )
  }

  // Signed in with workspace access - show app. The shell is keyed on the
  // workspace, so switching remounts every page and each one refetches under
  // the new X-Tenant-Id; nothing from the previous workspace stays on screen.
  return (
    <Routes>
      {/* A live dashboard on a wall shows the board alone, with no rail. */}
      <Route path="/live/:key" element={<WallboardPage key={currentWorkspace.id} />} />
      <Route
        path="/*"
        element={
          <Shell key={currentWorkspace.id}>
            <Routes>
              <Route path="/dashboard" element={gated('/dashboard', <DashboardPage />)} />
              <Route path="/personnel" element={gated('/personnel', <PersonnelPage />)} />
              <Route path="/groups" element={gated('/groups', <GroupsPage />)} />
              <Route path="/teams" element={gated('/teams', <TeamsPage />)} />
              <Route path="/timesheets" element={gated('/timesheets', <TimesheetsPage />)} />
              <Route path="/queries" element={gated('/queries', <QueriesPage />)} />
              <Route path="/wages" element={gated('/wages', <WagesPage />)} />
              <Route path="/product-activity" element={gated('/product-activity', <ProductActivityPage />)} />
              <Route path="/batches" element={gated('/batches', <BatchesPage />)} />
              <Route path="/reports" element={gated('/reports', <ReportsPage />)} />
              <Route path="/label-printing" element={gated('/label-printing', <LabelPrintingPage />)} />
              <Route path="/stock-dispatch/overview" element={gated('/stock-dispatch/overview', <OverviewPage />)} />
              <Route path="/stock-dispatch" element={gated('/stock-dispatch', <StockDispatchPage view="stock" />)} />
              <Route path="/stock-dispatch/orders" element={gated('/stock-dispatch/orders', <StockDispatchPage view="orders" />)} />
              <Route path="/stock-dispatch/dispatches" element={gated('/stock-dispatch/dispatches', <StockDispatchPage view="dispatches" />)} />
              <Route path="/stock-dispatch/settings" element={gated('/stock-dispatch/settings', <StockSettingsPage />)} />
              <Route path="/stock-dispatch/preview" element={gated('/stock-dispatch/preview', <ModulePreviewPage />)} />
              <Route path="/sites-jobs" element={gated('/sites-jobs', <SitesPage />)} />
              <Route path="/sites-map" element={gated('/sites-map', <SitesMapPage />)} />
              <Route path="/picking-jobs" element={gated('/picking-jobs', <PickingJobsPage />)} />
              <Route path="/devices" element={gated('/devices', <DevicesPage />)} />
              <Route path="/integrations" element={gated('/integrations', <IntegrationsPage />)} />
              <Route path="/integrations/api-keys" element={gated('/integrations/api-keys', <ApiKeysPage />)} />
              <Route path="/quality-control" element={gated('/quality-control', <QualityControlPage />)} />
              <Route path="/settings" element={gated('/settings', <GeneralSettingsPage />)} />
              <Route path="/" element={<Navigate to={landingPath(access)} replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Shell>
        }
      />
    </Routes>
  )
}

// Each screen behind the area that opens it (lib/access.ts SCREENS).
function gated(path: string, element: React.ReactElement) {
  const screen = screenOf(path)
  if (!screen) throw new Error(`${path} has no entry in lib/access.ts SCREENS`)
  return (
    <AccessGate gate={screen.gate} probe={screen.probe}>
      {element}
    </AccessGate>
  )
}

// An address inside the shell that names no screen (an old bookmark, a typo)
// says so, instead of drawing an empty page beside the rail.
function NotFoundPage() {
  return (
    <div style={styles.placeholderContainer}>
      <h1>No screen here</h1>
      <p>
        Nothing in Control lives at this address. <Link to="/dashboard">Go to Dashboard</Link>
      </p>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: 'var(--dp-surface)',
  },
  spinner: {
    width: '2.5rem',
    height: '2.5rem',
    border: '3px solid var(--dp-line)',
    borderTopColor: 'var(--dp-accent)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '1.5rem',
  },
  placeholderContainer: {
    padding: '1.5rem',
    textAlign: 'center',
  },
}

export default App
